import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useVehicleSettingsOperations } from "../../../hooks/useVehicleSettingsOperations";
import { useVehicleTracking } from "../../../hooks/useVehicleTracking";
import { useTrackingProviders } from "../../../context/TrackingProvidersContext";
import { useNotification } from "../../../context/NotificationContext";
import LoadingAnimation from "../../loading/LoadingAnimation";
import { useLayout } from "../../../context/LayoutContext";
import IconClose from "../../../assets/svg/layout/close.svg";
import "../../../assets/scss/components/ui/tables/LocationReportSettings.scss"

// TimeScheduler component remains the same
function TimeScheduler({ schedules, onChange, theme }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const handleToggleHour = (hour) => {
    const newSchedules = schedules.includes(hour)
      ? schedules.filter(h => h !== hour)
      : [...schedules, hour].sort((a, b) => a - b);
    onChange(newSchedules);
  };
  
  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };
  
  return (
    <div className={`LRS__${theme}-time-scheduler`}>
      <div className={`LRS__${theme}-hours-grid`} role="group" aria-label="Seleccionar horarios para reportes">
        {hours.map((hour) => (
          <button
            key={hour}
            type="button"
            className={`LRS__${theme}-hour-chip ${schedules.includes(hour) ? 'active' : ''}`}
            onClick={() => handleToggleHour(hour)}
            aria-pressed={schedules.includes(hour)}
            aria-label={`Horario ${formatHour(hour)}`}
          >
            {formatHour(hour)}
          </button>
        ))}
      </div>
    </div>
  );
}

TimeScheduler.propTypes = {
  schedules: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired
};

// OperatorModal component remains the same
function OperatorModal({ 
  isOpen, 
  onClose, 
  vehicle, 
  operators, 
  onSave, 
  theme 
}) {
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [departureTime, setDepartureTime] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    if (vehicle?.operator) {
      setSelectedOperator(vehicle.operator.id);
      setDepartureTime(vehicle.departure_time || '');
    } else {
      setSelectedOperator(null);
      setDepartureTime('');
    }
    setSearchTerm('');
  }, [vehicle, isOpen]);
  
  const filteredOperators = React.useMemo(() => {
    if (!searchTerm) return operators;
    
    const searchLower = searchTerm.toLowerCase();
    return operators.filter(operator => 
      `${operator.first_name} ${operator.last_name}`.toLowerCase().includes(searchLower) ||
      operator.identification_number.toLowerCase().includes(searchLower)
    );
  }, [operators, searchTerm]);
  
  const handleSubmit = () => {
    if (!selectedOperator || !departureTime) {
      return;
    }
    onSave(vehicle, selectedOperator, departureTime);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={`LRS__${theme}-modal-overlay`}>
      <div className={`LRS__${theme}-modal`}>
        <header className={`LRS__${theme}-modal-header`}>
          <h3>Configurar Vehículo: {vehicle?.plate}</h3>
          <button 
            type="button"
            className={`LRS__${theme}-modal-close`} 
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <img src={IconClose} alt="Cerrar" />
          </button>
        </header>
        
        <div className={`LRS__${theme}-modal-content`}>
          <div className={`LRS__${theme}-vehicle-info-section`}>
            <h4>Información del Vehículo</h4>
            <div className={`LRS__${theme}-info-grid`}>
              <div className={`LRS__${theme}-info-item`}>
                <label>Placa:</label>
                <span>{vehicle?.plate}</span>
              </div>
              <div className={`LRS__${theme}-info-item`}>
                <label>Proveedor:</label>
                <span>{vehicle?.provider}</span>
              </div>
            </div>
          </div>
          
          <div className={`LRS__${theme}-form-group`}>
            <label htmlFor="operator-search">Buscar Operador</label>
            <div className={`LRS__${theme}-search-wrapper`}>
              <input
                id="operator-search"
                type="text"
                placeholder="Buscar por nombre o cédula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`LRS__${theme}-form-input`}
              />
              <i className="fas fa-search"></i>
            </div>
            <small className={`LRS__${theme}-form-hint`}>
              {filteredOperators.length} operador(es) disponible(s)
            </small>
          </div>
          
          <div className={`LRS__${theme}-form-group`}>
            <label htmlFor="operator-select">Operador Asignado</label>
            <select
              id="operator-select"
              value={selectedOperator || ''}
              onChange={(e) => setSelectedOperator(e.target.value)}
              className={`LRS__${theme}-form-select`}
            >
              <option value="">Seleccione un operador</option>
              {filteredOperators.map(operator => (
                <option key={operator.id} value={operator.id}>
                  {operator.first_name} {operator.last_name} {operator.second_last_name || ''} - C.C. {operator.identification_number}
                </option>
              ))}
            </select>
            {filteredOperators.length === 0 && searchTerm && (
              <small className={`LRS__${theme}-form-error`}>
                No se encontraron operadores con ese criterio de búsqueda
              </small>
            )}
          </div>
          
          {selectedOperator && (
            <div className={`LRS__${theme}-selected-operator-info`}>
              <h5>Información del Operador Seleccionado</h5>
              {(() => {
                const operator = operators.find(op => op.id === selectedOperator);
                return operator ? (
                  <div className={`LRS__${theme}-operator-details`}>
                    <p><strong>Nombre:</strong> {operator.first_name} {operator.last_name} {operator.second_last_name || ''}</p>
                    <p><strong>Cédula:</strong> {operator.identification_number}</p>
                    <p><strong>Teléfono:</strong> {operator.phone_number}</p>
                    {operator.email && <p><strong>Email:</strong> {operator.email}</p>}
                  </div>
                ) : null;
              })()}
            </div>
          )}
          
          <div className={`LRS__${theme}-form-group`}>
            <label htmlFor="departure-time">Hora de Salida</label>
            <input
              id="departure-time"
              type="time"
              value={departureTime}
              onChange={(e) => setDepartureTime(e.target.value)}
              className={`LRS__${theme}-form-input`}
            />
            <small className={`LRS__${theme}-form-hint`}>
              Hora programada de inicio de operación del vehículo
            </small>
          </div>
          
          {(!selectedOperator || !departureTime) && (
            <div className={`LRS__${theme}-validation-warning`}>
              <i className="fas fa-exclamation-triangle"></i>
              Complete todos los campos para configurar el vehículo
            </div>
          )}
          
          <div className={`LRS__${theme}-form-actions`}>
            <button
              type="button"
              className={`LRS__${theme}-button-secondary`}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={`LRS__${theme}-button-primary`}
              onClick={handleSubmit}
              disabled={!selectedOperator || !departureTime}
            >
              Guardar Configuración
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

OperatorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicle: PropTypes.shape({
    plate: PropTypes.string,
    provider: PropTypes.string,
    operator: PropTypes.shape({
      id: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      second_last_name: PropTypes.string,
      identification_number: PropTypes.string,
      phone_number: PropTypes.string,
      email: PropTypes.string
    }),
    departure_time: PropTypes.string
  }),
  operators: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    second_last_name: PropTypes.string,
    identification_number: PropTypes.string.isRequired,
    phone_number: PropTypes.string.isRequired,
    email: PropTypes.string
  })).isRequired,
  onSave: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired
};

export function LocationReportSettings() {
  // Estados principales del componente
  const [activeTab, setActiveTab] = useState("schedules");
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [reportSchedules, setReportSchedules] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [operators, setOperators] = useState([]);
  const [scheduledVehicles, setScheduledVehicles] = useState([]);
  
  // Estados de paginación
  const [vehicleGridPage, setVehicleGridPage] = useState(0);
  const [selectedVehiclesPage, setSelectedVehiclesPage] = useState(0);
  
  // Hooks de contexto y servicios
  const { theme, selectedDate, getCurrentDate } = useLayout();
  const { showNotification } = useNotification();
  const { providers } = useTrackingProviders();
  
  // 🔄 ACTUALIZADO: Usar el nuevo hook de vehicle tracking
  const { 
    vehicles: allVehicles, 
    isLoading: vehiclesLoading,
    isConnected,
    connectionStatus,
    lastUpdate
  } = useVehicleTracking(selectedProvider);
  
  // Hooks de operaciones vehiculares
  const { 
    useLocationReportConfig,
    useUpdateReportVehicles,
    useUpdateReportSchedules,
    useProcessLocationReports,
    useConfigureVehicleWithOperator
  } = useVehicleSettingsOperations();
  
  // Queries y mutations
  const { 
    data: configData, 
    isLoading: configLoading, 
    refetch: refetchConfig 
  } = useLocationReportConfig(selectedDate);
  
  const updateVehiclesMutation = useUpdateReportVehicles();
  const updateSchedulesMutation = useUpdateReportSchedules();
  const processReportsMutation = useProcessLocationReports();
  const configureVehicleMutation = useConfigureVehicleWithOperator();
  
  // Configuración de paginación
  const GRID_COLUMNS = 6;
  const GRID_ROWS = 2;
  const ITEMS_PER_PAGE = GRID_COLUMNS * GRID_ROWS;
  
  // Efecto para cargar datos de configuración cuando cambian
  useEffect(() => {
    if (configData?.data) {
      const { vehicles, schedules, operators, scheduledVehicles, queryDate } = configData.data;
      setSelectedVehicles(vehicles || []);
      setReportSchedules(schedules || []);
      setOperators(operators || []);
      setScheduledVehicles(scheduledVehicles || []);
    }
  }, [configData]);

  // Efecto para recargar configuración cuando cambie la fecha seleccionada
  useEffect(() => {
    refetchConfig();
  }, [selectedDate, refetchConfig]);
  
  // Filtrado de vehículos disponibles basado en búsqueda
  const filteredVehicles = React.useMemo(() => {
    if (!allVehicles) return [];
    return allVehicles.filter(v => {
      const searchString = searchTerm.toLowerCase();
      return (
        (v.UnitId?.toLowerCase().includes(searchString)) ||
        (v.UnitPlate?.toLowerCase().includes(searchString)) ||
        (v.Placa?.toLowerCase().includes(searchString))
      );
    });
  }, [allVehicles, searchTerm]);
  
  // Cálculos de paginación
  const vehicleGridTotalPages = Math.ceil(filteredVehicles.length / ITEMS_PER_PAGE);
  const selectedVehiclesTotalPages = Math.ceil(selectedVehicles.length / ITEMS_PER_PAGE);
  
  const visibleGridVehicles = filteredVehicles.slice(
    vehicleGridPage * ITEMS_PER_PAGE,
    (vehicleGridPage + 1) * ITEMS_PER_PAGE
  );
  
  const visibleSelectedVehicles = selectedVehicles.slice(
    selectedVehiclesPage * ITEMS_PER_PAGE,
    (selectedVehiclesPage + 1) * ITEMS_PER_PAGE
  );
  
  // Manejo de selección/deselección de vehículos
  const handleVehicleToggle = (vehicle) => {
    const vehicleIdentifier = {
      plate: vehicle.UnitPlate || vehicle.Placa,
      provider: selectedProvider === 'all' ? vehicle.Provider : selectedProvider,
    };
    
    const exists = selectedVehicles.find(
      v => v.plate === vehicleIdentifier.plate && v.provider === vehicleIdentifier.provider
    );
    
    if (exists) {
      if (exists.is_configured) {
        if (window.confirm('Este vehículo ya está configurado. ¿Desea eliminarlo de la lista?')) {
          setSelectedVehicles(selectedVehicles.filter(
            v => !(v.plate === vehicleIdentifier.plate && v.provider === vehicleIdentifier.provider)
          ));
        }
      } else {
        setSelectedVehicles(selectedVehicles.filter(
          v => !(v.plate === vehicleIdentifier.plate && v.provider === vehicleIdentifier.provider)
        ));
      }
    } else {
      setSelectedVehicles([...selectedVehicles, {
        ...vehicleIdentifier,
        is_configured: false,
        operator_id: null,
        departure_time: null,
        tempId: `temp-${Date.now()}`
      }]);
    }
  };
  
  // Función para eliminar vehículo de la lista de seleccionados
  const handleRemoveSelectedVehicle = (vehicleToRemove) => {
    setSelectedVehicles(selectedVehicles.filter(
      v => !(v.plate === vehicleToRemove.plate && v.provider === vehicleToRemove.provider)
    ));
  };
  
  // Guardar configuración según la pestaña activa
  const handleSaveConfiguration = async () => {
    try {
      if (activeTab === "schedules") {
        await updateSchedulesMutation.mutateAsync(reportSchedules);
      } else {
        const unconfiguredVehicles = selectedVehicles.filter(v => !v.is_configured);
        if (unconfiguredVehicles.length > 0) {
          showNotification({
            message: `Hay ${unconfiguredVehicles.length} vehículos sin configurar. Configure operador y hora de salida para todos.`,
            severity: "warning"
          });
          return;
        }
        
        await updateVehiclesMutation.mutateAsync(selectedVehicles);
      }
      
      showNotification({
        message: "Configuración guardada correctamente",
        severity: "success"
      });
      
      refetchConfig();
    } catch (error) {
      showNotification({
        message: `Error al guardar configuración: ${error.message}`,
        severity: "error"
      });
    }
  };
  
  // Procesar reportes de ubicación
  const handleProcessReports = async () => {
    setIsProcessing(true);
    try {
      await processReportsMutation.mutateAsync();
      showNotification({
        message: "Procesamiento de reportes iniciado",
        severity: "success"
      });
    } catch (error) {
      showNotification({
        message: `Error al procesar reportes: ${error.message}`,
        severity: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Guardar configuración de vehículo con operador
  const handleSaveVehicleOperator = async (vehicleData, operatorId, departureTime) => {
    try {
      const requestData = {
        vehicle: {
          plate: vehicleData.plate,
          provider: vehicleData.provider,
        },
        operatorId,
        departureTime
      };
      
      const response = await configureVehicleMutation.mutateAsync(requestData);
      
      setSelectedVehicles(prev => 
        prev.map(vehicle => 
          vehicle.plate === vehicleData.plate && vehicle.provider === vehicleData.provider
            ? { ...response.data }
            : vehicle
        )
      );
      
      setEditingVehicle(null);
      
      showNotification({
        message: "Vehículo configurado correctamente",
        severity: "success"
      });
    } catch (error) {
      showNotification({
        message: `Error al configurar vehículo: ${error.message}`,
        severity: "error"
      });
    }
  };
  
  // Función para abrir modal de edición de vehículo
  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
  };

  // 🎨 NUEVO: Obtener estado de conexión con estilos
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return isConnected ? '🟢 Conectado en tiempo real' : '🟢 Conectado';
      case 'connecting':
        return '🟡 Conectando...';
      case 'disconnected':
        return '🔴 Desconectado';
      default:
        return '⚪ Estado desconocido';
    }
  };

  // 🎨 NUEVO: Formatear última actualización
  const getLastUpdateText = () => {
    if (!lastUpdate) return 'Nunca';
    
    const updateTime = new Date(lastUpdate);
    const now = new Date();
    const diffSeconds = Math.floor((now - updateTime) / 1000);
    
    if (diffSeconds < 60) {
      return `Hace ${diffSeconds} segundos`;
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `Hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    } else {
      return updateTime.toLocaleTimeString('es-CO');
    }
  };
  
  // Mostrar loading mientras cargan los datos
  if (configLoading || vehiclesLoading) {
    return <LoadingAnimation />;
  }
  
  return (
    <div className={`LRS__${theme}-container`}>
      <header className={`LRS__${theme}-header`}>
        <div className={`LRS__${theme}-header-main`}>
          <h2 className={`LRS__${theme}-title`}>Configuración de Reporte de Ubicación</h2>
          <div className={`LRS__${theme}-date-info`}>
            <i className="fas fa-calendar-alt"></i>
            <span>Configurando para: {getCurrentDate}</span>
          </div>
          {/* 🆕 NUEVO: Indicador de estado de conexión */}
          <div className={`LRS__${theme}-connection-status`}>
            <span>{getConnectionStatusText()}</span>
            {lastUpdate && (
              <small>• {getLastUpdateText()}</small>
            )}
          </div>
        </div>
        
        <nav className={`LRS__${theme}-navigation`}>
          <button
            type="button"
            className={`LRS__${theme}-nav-button ${activeTab === "schedules" ? "active" : ""}`}
            onClick={() => setActiveTab("schedules")}
          >
            <i className="fas fa-clock"></i>
            Horarios
          </button>
          <button
            type="button"
            className={`LRS__${theme}-nav-button ${activeTab === "vehicles" ? "active" : ""}`}
            onClick={() => setActiveTab("vehicles")}
          >
            <i className="fas fa-truck"></i>
            Vehículos
            {/* 🆕 NUEVO: Mostrar indicador de disponibilidad */}
            {!isConnected && (
              <span className={`LRS__${theme}-connection-indicator offline`}>
                ⚠️
              </span>
            )}
          </button>
          <button
            type="button"
            className={`LRS__${theme}-nav-button ${activeTab === "scheduled" ? "active" : ""}`}
            onClick={() => setActiveTab("scheduled")}
          >
            <i className="fas fa-calendar-alt"></i>
            Programados ({scheduledVehicles.length})
          </button>
        </nav>
        
        <button
          type="button"
          className={`LRS__${theme}-process-button`}
          onClick={handleProcessReports}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <span className={`LRS__${theme}-processing`}>
              <i className="fas fa-spinner fa-spin"></i>
              Procesando...
            </span>
          ) : (
            <>
              <i className="fas fa-play"></i>
              Procesar Reportes
            </>
          )}
        </button>
      </header>
      
      <div className={`LRS__${theme}-content`}>
        {/* Panel de Horarios */}
        {activeTab === "schedules" && (
          <section className={`LRS__${theme}-schedules-section`}>
            <h3 className={`LRS__${theme}-section-title`}>
              Seleccione los horarios para enviar reportes
            </h3>
            <TimeScheduler
              schedules={reportSchedules}
              onChange={setReportSchedules}
              theme={theme}
            />
            <button
              type="button"
              className={`LRS__${theme}-save-button`}
              onClick={handleSaveConfiguration}
              disabled={updateSchedulesMutation.isLoading}
            >
              {updateSchedulesMutation.isLoading ? 
                'Guardando...' : 'Guardar Configuración'}
            </button>
          </section>
        )}
        
        {/* Panel de Vehículos Configurados Manualmente */}
        {activeTab === "vehicles" && (
          <section className={`LRS__${theme}-vehicles-section`}>
            {/* 🆕 NUEVO: Banner de estado de conexión para vehículos */}
            {!isConnected && (
              <div className={`LRS__${theme}-warning-banner`}>
                <span className={`LRS__${theme}-warning-icon`}>⚠️</span>
                <span className={`LRS__${theme}-warning-text`}>
                  Sin conexión en tiempo real. Mostrando datos en caché. 
                  Los vehículos se actualizarán automáticamente al restaurarse la conexión.
                </span>
              </div>
            )}

            <div className={`LRS__${theme}-vehicles-controls`}>
              <div className={`LRS__${theme}-control-group`}>
                <label htmlFor="provider-select" className="sr-only">Seleccionar proveedor</label>
                <select
                  id="provider-select"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  className={`LRS__${theme}-provider-select`}
                  disabled={vehiclesLoading}
                >
                  <option value="all">Todos los proveedores</option>
                  {providers.filter(p => p.id !== 'all').map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={`LRS__${theme}-search-wrapper`}>
                <label htmlFor="search-vehicles" className="sr-only">Buscar vehículos</label>
                <i className="fas fa-search"></i>
                <input
                  id="search-vehicles"
                  type="text"
                  placeholder="Buscar por placa o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`LRS__${theme}-search-input`}
                />
              </div>

              {/* 🆕 NUEVO: Información de estado */}
              <div className={`LRS__${theme}-status-info`}>
                <span className={`LRS__${theme}-vehicle-count`}>
                  {allVehicles.length} vehículos disponibles
                </span>
                <span className={`LRS__${theme}-connection-indicator ${isConnected ? 'online' : 'offline'}`}>
                  {isConnected ? '🟢' : '🔴'}
                </span>
              </div>
            </div>
            
            {/* Grid de selección de vehículos */}
            <div className={`LRS__${theme}-vehicles-grid-container`}>
              {vehiclesLoading && allVehicles.length === 0 ? (
                <div className={`LRS__${theme}-loading-vehicles`}>
                  <div className={`LRS__${theme}-loading-spinner`}></div>
                  <p>
                    {connectionStatus === 'connecting' 
                      ? 'Conectando con el servidor...' 
                      : 'Cargando vehículos...'}
                  </p>
                </div>
              ) : (
                <div className={`LRS__${theme}-vehicles-grid`}>
                  {visibleGridVehicles.map((vehicle) => {
                    const selectedVehicle = selectedVehicles.find(
                      v => v.plate === (vehicle.UnitPlate || vehicle.Placa) && 
                           v.provider === (selectedProvider === 'all' ? vehicle.Provider : selectedProvider)
                    );
                    
                    const isSelected = !!selectedVehicle;
                    const vehicleId = `vehicle-${vehicle.UnitId || vehicle.Imei}`;
                    
                    return (
                      <div
                        key={vehicleId}
                        className={`LRS__${theme}-vehicle-card ${isSelected ? 'selected' : ''}`}
                      >
                        <div className={`LRS__${theme}-vehicle-header`}>
                          <div className={`LRS__${theme}-vehicle-checkbox`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleVehicleToggle(vehicle)}
                              id={vehicleId}
                            />
                            <label htmlFor={vehicleId}></label>
                          </div>
                          
                          {isSelected && (
                            <div className={`LRS__${theme}-vehicle-actions`}>
                              <button
                                type="button"
                                className={`LRS__${theme}-action-button`}
                                onClick={() => handleEditVehicle(selectedVehicle)}
                                title="Editar"
                                aria-label={`Editar vehículo ${vehicle.UnitPlate || vehicle.Placa}`}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              
                              <div className={`LRS__${theme}-tooltip-wrapper`}>
                                <button
                                  type="button"
                                  className={`LRS__${theme}-action-button`}
                                  title="Información"
                                  aria-label={`Información del vehículo ${vehicle.UnitPlate || vehicle.Placa}`}
                                >
                                  <i className="fas fa-question-circle"></i>
                                </button>
                                
                                {selectedVehicle.is_configured && selectedVehicle.operator && (
                                  <div className={`LRS__${theme}-tooltip`}>
                                    <p><strong>Operador:</strong> {selectedVehicle.operator.first_name} {selectedVehicle.operator.last_name}</p>
                                    <p><strong>Identificación:</strong> {selectedVehicle.operator.identification_number}</p>
                                    <p><strong>Teléfono:</strong> {selectedVehicle.operator.phone_number}</p>
                                    <p><strong>Hora de Salida:</strong> {selectedVehicle.departure_time}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className={`LRS__${theme}-vehicle-info`}>
                          <span className={`LRS__${theme}-vehicle-plate`}>
                            {vehicle.UnitPlate || vehicle.Placa}
                          </span>
                          <span className={`LRS__${theme}-vehicle-id`}>
                            ID: {vehicle.UnitId || vehicle.Imei}
                          </span>
                          {isSelected && !selectedVehicle.is_configured && (
                            <span className={`LRS__${theme}-vehicle-status warning`}>
                              Pendiente configuración
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Paginación del grid de vehículos */}
              {vehicleGridTotalPages > 1 && (
                <div className={`LRS__${theme}-pagination`}>
                  <button
                    type="button"
                    className={`LRS__${theme}-pagination-button`}
                    onClick={() => setVehicleGridPage(prev => Math.max(0, prev - 1))}
                    disabled={vehicleGridPage === 0}
                    aria-label="Página anterior"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className={`LRS__${theme}-pagination-info`}>
                    Página {vehicleGridPage + 1} de {vehicleGridTotalPages}
                  </span>
                  <button
                    type="button"
                    className={`LRS__${theme}-pagination-button`}
                    onClick={() => setVehicleGridPage(prev => Math.min(vehicleGridTotalPages - 1, prev + 1))}
                    disabled={vehicleGridPage === vehicleGridTotalPages - 1}
                    aria-label="Página siguiente"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
            
            {/* Resumen de vehículos seleccionados */}
            <div className={`LRS__${theme}-selected-summary`}>
              <h3 className={`LRS__${theme}-summary-title`}>
                Vehículos Seleccionados: {selectedVehicles.length}
                <span className={`LRS__${theme}-configured-count`}>
                  ({selectedVehicles.filter(v => v.is_configured).length} configurados)
                </span>
              </h3>
              
              <div className={`LRS__${theme}-selected-vehicles-container`}>
                <div className={`LRS__${theme}-selected-vehicles`}>
                  {visibleSelectedVehicles.map((vehicle, index) => (
                    <span key={vehicle.tempId || index} className={`LRS__${theme}-selected-vehicle ${!vehicle.is_configured ? 'unconfigured' : ''}`}>
                      {vehicle.plate} ({vehicle.provider})
                      <button
                        type="button"
                        className={`LRS__${theme}-remove-vehicle`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSelectedVehicle(vehicle);
                        }}
                        aria-label={`Eliminar vehículo ${vehicle.plate}`}
                      >
                        <img src={IconClose} alt="Eliminar" />
                      </button>
                    </span>
                  ))}
                </div>
                
                {/* Paginación de vehículos seleccionados */}
                {selectedVehiclesTotalPages > 1 && (
                  <div className={`LRS__${theme}-pagination`}>
                    <button
                      type="button"
                      className={`LRS__${theme}-pagination-button`}
                      onClick={() => setSelectedVehiclesPage(prev => Math.max(0, prev - 1))}
                      disabled={selectedVehiclesPage === 0}
                      aria-label="Página anterior de vehículos seleccionados"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <span className={`LRS__${theme}-pagination-info`}>
                      Página {selectedVehiclesPage + 1} de {selectedVehiclesTotalPages}
                    </span>
                    <button
                      type="button"
                      className={`LRS__${theme}-pagination-button`}
                      onClick={() => setSelectedVehiclesPage(prev => Math.min(selectedVehiclesTotalPages - 1, prev + 1))}
                      disabled={selectedVehiclesPage === selectedVehiclesTotalPages - 1}
                      aria-label="Página siguiente de vehículos seleccionados"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <button
              type="button"
              className={`LRS__${theme}-save-button`}
              onClick={handleSaveConfiguration}
              disabled={updateVehiclesMutation.isLoading}
            >
              {updateVehiclesMutation.isLoading ? 
                'Guardando...' : 'Guardar Configuración'}
            </button>
          </section>
        )}
        
        {/* Panel de Vehículos Programados */}
        {activeTab === "scheduled" && (
          <section className={`LRS__${theme}-scheduled-section`}>
            <h3 className={`LRS__${theme}-section-title`}>
              Vehículos en Programación Vehicular
              <span className={`LRS__${theme}-section-subtitle`}>
                Fecha consultada: {selectedDate} | {scheduledVehicles.length} vehículo(s) encontrado(s)
              </span>
            </h3>
            
            {scheduledVehicles.length === 0 ? (
              <div className={`LRS__${theme}-empty-message`}>
                <i className="fas fa-calendar-times"></i>
                <p>No hay vehículos programados para {getCurrentDate}</p>
                <small>Los vehículos programados aparecerán aquí automáticamente según la programación vehicular</small>
                <small>Puede cambiar la fecha desde el selector en el header para ver otras fechas</small>
              </div>
            ) : (
              <>
                <div className={`LRS__${theme}-scheduled-summary`}>
                  <div className={`LRS__${theme}-summary-stats`}>
                    <div className={`LRS__${theme}-stat-item`}>
                      <i className="fas fa-truck"></i>
                      <span>{scheduledVehicles.length} Vehículos</span>
                    </div>
                    <div className={`LRS__${theme}-stat-item`}>
                      <i className="fas fa-route"></i>
                      <span>{new Set(scheduledVehicles.map(v => v.operationName)).size} Operaciones</span>
                    </div>
                    <div className={`LRS__${theme}-stat-item`}>
                      <i className="fas fa-clock"></i>
                      <span>Automático</span>
                    </div>
                  </div>
                </div>
                
                <div className={`LRS__${theme}-scheduled-vehicles-grid`}>
                  {scheduledVehicles.map((scheduledVehicle, index) => (
                    <div key={`${scheduledVehicle.vehicleId}-${index}`} className={`LRS__${theme}-scheduled-vehicle-card`}>
                      <div className={`LRS__${theme}-scheduled-header`}>
                        <h4>{scheduledVehicle.vehicleId}</h4>
                        <span className={`LRS__${theme}-position-badge`}>
                          Posición #{scheduledVehicle.position + 1}
                        </span>
                      </div>
                      
                      <div className={`LRS__${theme}-scheduled-info`}>
                        <div className={`LRS__${theme}-info-row`}>
                          <strong>Operación:</strong>
                          <span>{scheduledVehicle.operationName}</span>
                        </div>
                        <div className={`LRS__${theme}-info-row`}>
                          <strong>Ruta:</strong>
                          <span>{scheduledVehicle.routeName}</span>
                        </div>
                        <div className={`LRS__${theme}-info-row`}>
                          <strong>Origen:</strong>
                          <span>{scheduledVehicle.loadingPoint}</span>
                        </div>
                        <div className={`LRS__${theme}-info-row`}>
                          <strong>Destino:</strong>
                          <span>{scheduledVehicle.unloadingPoint}</span>
                        </div>
                        <div className={`LRS__${theme}-info-row`}>
                          <strong>Distancia:</strong>
                          <span>{scheduledVehicle.distance} km</span>
                        </div>
                        <div className={`LRS__${theme}-info-row`}>
                          <strong>Salida:</strong>
                          <span>{scheduledVehicle.departureTime || 'No definida'}</span>
                        </div>
                      </div>
                      
                      <div className={`LRS__${theme}-scheduled-status`}>
                        <i className="fas fa-calendar-check"></i>
                        <span>Programado para {scheduledVehicle.scheduledDate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            <div className={`LRS__${theme}-scheduled-info-box`}>
              <h4>Información sobre Vehículos Programados</h4>
              <ul>
                <li>Los vehículos programados se incluyen automáticamente en el reporte de ubicación</li>
                <li>No requieren configuración manual de operador ni hora de salida</li>
                <li>La información mostrada proviene de la programación vehicular del día seleccionado</li>
                <li>Solo se muestran fechas desde la actual hacia el futuro</li>
                <li>Cambie la fecha desde el selector en el header para ver otras programaciones</li>
              </ul>
            </div>
          </section>
        )}
      </div>
      
      {/* Modal de configuración de operador */}
      {editingVehicle && (
        <OperatorModal
          isOpen={!!editingVehicle}
          onClose={() => setEditingVehicle(null)}
          vehicle={editingVehicle}
          operators={operators}
          onSave={(vehicleData, operatorId, departureTime) => 
            handleSaveVehicleOperator(vehicleData, operatorId, departureTime)
          }
          theme={theme}
        />
      )}
    </div>
  );
}